import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Outlet } from "react-router-dom";
import { Box, Drawer, useTheme, useMediaQuery } from "@mui/material";
import { logout } from "../redux/slices/userSlice";
import { fetchNotifications } from "../redux/slices/notificationSlice";
import { useSocket } from "../contexts/SocketContext";
import Sidebar from "../components/layout/Sidebar";
import TopBar from "../components/layout/TopBar";
import UserProfileMenu from "../components/layout/UserProfileMenu";
import NotificationsMenu from "../components/layout/NotificationsMenu";
import { LAYOUT } from "../theme/constants";
import { getPagePadding } from "../theme/responsive";
import VerificationBanner from "../components/VerificationBanner";
import PastDueBanner from "../components/billing/PastDueBanner";
import UsageAlertBanner from "../components/usage/UsageAlertBanner";

const DashboardLayout = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const _isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const userState = useSelector((state) => state.user);
  const { unreadCount } = useSelector((state) => state.notification);
  const { socket } = useSocket();

  // Fetch notifications on mount and periodically
  useEffect(() => {
    if (userState?.isAuthenticated) {
      dispatch(fetchNotifications({ limit: 20 }));

      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        dispatch(fetchNotifications({ limit: 20 }));
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [dispatch, userState?.isAuthenticated]);

  // Listen for socket events that generate notifications to refresh immediately
  useEffect(() => {
    if (!socket || !userState?.isAuthenticated) return;

    const refreshNotifications = () => {
      dispatch(fetchNotifications({ limit: 20 }));
    };

    // Events that may generate new notifications
    socket.on("site_status", refreshNotifications);
    socket.on("siteCreationProgress", refreshNotifications);
    socket.on("backup_status", refreshNotifications);
    socket.on("backup_completed", refreshNotifications);
    socket.on("site_deleted", refreshNotifications);
    socket.on("site_restored", refreshNotifications);
    socket.on("deployment_started", refreshNotifications);
    socket.on("deployment_completed", refreshNotifications);
    socket.on("deployment_failed", refreshNotifications);
    socket.on("new_notification", refreshNotifications);

    return () => {
      socket.off("site_status", refreshNotifications);
      socket.off("siteCreationProgress", refreshNotifications);
      socket.off("backup_status", refreshNotifications);
      socket.off("backup_completed", refreshNotifications);
      socket.off("site_deleted", refreshNotifications);
      socket.off("site_restored", refreshNotifications);
      socket.off("deployment_started", refreshNotifications);
      socket.off("deployment_completed", refreshNotifications);
      socket.off("deployment_failed", refreshNotifications);
      socket.off("new_notification", refreshNotifications);
    };
  }, [socket, dispatch, userState?.isAuthenticated]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    setTimeout(() => navigate("/auth"), 100);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      {/* Top Bar */}
      <TopBar
        drawerWidth={LAYOUT.sidebarWidth}
        user={userState?.user}
        notificationCount={unreadCount}
        onMenuClick={handleDrawerToggle}
        onNotificationClick={(e) => setNotificationAnchorEl(e.currentTarget)}
        onProfileClick={(e) => setProfileAnchorEl(e.currentTarget)}
      />

      {/* Notifications Menu */}
      <NotificationsMenu
        anchorEl={notificationAnchorEl}
        onClose={() => setNotificationAnchorEl(null)}
      />

      {/* Profile Menu */}
      <UserProfileMenu
        anchorEl={profileAnchorEl}
        onClose={() => setProfileAnchorEl(null)}
        onLogout={handleLogout}
      />

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: LAYOUT.sidebarWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: LAYOUT.sidebarWidth,
            },
          }}
        >
          <Sidebar
            user={userState?.user}
            isMobile={true}
            onNavigate={handleDrawerToggle}
          />
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: LAYOUT.sidebarWidth,
            },
          }}
          open
        >
          <Sidebar user={userState?.user} isMobile={false} />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${LAYOUT.sidebarWidth}px)` },
          mt: `${LAYOUT.topBarHeight}px`,
          minHeight: "100vh",
          transition: "all 0.15s ease",
        }}
      >
        <VerificationBanner />
        <PastDueBanner />
        <UsageAlertBanner />
        <Box sx={{ p: getPagePadding() }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
